import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { dbManager } from '../services/db_manager';
import { useAuth } from '../contexts/AuthContext';
import { BrainCircuit, ArrowLeft, Globe2, Sparkles, CheckCircle2, ChevronRight, BarChart2, Loader2, RefreshCw } from 'lucide-react';
import { AnimatedIcon } from '../components/AnimatedIcon';
import { StudyPageHeader } from '../components/StudyPageHeader';
import { AppBackButton, goBackOr, canGoBack } from '../components/AppBackButton';
import T from '../components/T';
import i18nInstance from '../i18n';

const AVAILABLE_LANGS = [
  { code: 'en', name: 'Inglês', flag: '🇺🇸' },
  { code: 'es', name: 'Espanhol', flag: '🇪🇸' },
  { code: 'fr', name: 'Francês', flag: '🇫🇷' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'de', name: 'Alemão', flag: '🇩🇪' },
  { code: 'pt', name: 'Português', flag: '🇧🇷' },
  { code: 'jp', name: 'Japonês', flag: '🇯🇵' },
  { code: 'cn', name: 'Mandarim', flag: '🇨🇳' },
  { code: 'ru', name: 'Russo', flag: '🇷🇺' },
  { code: 'hu', name: 'Húngaro', flag: '🇭🇺' }
];

const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
const CEFR_MAX_WORDS: Record<string, number> = {
  'A1': 1500,
  'A2': 3000,
  'B1': 5000,
  'B2': 10000,
  'C1': 16000,
  'C2': 25000
};

// Helper: Shuffle array
function shuffle<T>(array: T[]): T[] {
  const newArr = [...array];
  for (let i = newArr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArr[i], newArr[j]] = [newArr[j], newArr[i]];
  }
  return newArr;
}

const LevelTestPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [step, setStep] = useState<0 | 1 | 2 | 3 | 4>(0); // 0: Intro, 1: Step 1, 2: Step 2, 3: Saving, 4: Result
  const [selectedLang, setSelectedLang] = useState<string>('en');
  
  const [history, setHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  // Test State
  const [testData, setTestData] = useState<Record<string, string[]>>({});
  const [wordLevelMap, setWordLevelMap] = useState<Record<string, string>>({});
  const [step1Words, setStep1Words] = useState<string[]>([]);
  const [step2Words, setStep2Words] = useState<string[]>([]);
  
  // Set to store known words globally
  const [knownWords, setKnownWords] = useState<Set<string>>(new Set());
  
  const [finalResult, setFinalResult] = useState<{ cefr: string, words: number } | null>(null);

  useEffect(() => {
    if (user?.id && step === 0) {
      loadHistory();
    }
  }, [user?.id, step]);

  const loadHistory = async () => {
    if (!user?.id) return;
    setLoadingHistory(true);
    try {
      const res = await dbManager.from('user_language_tests', user.id).select({}, { orderBy: 'created_at DESC' });
      setHistory(Array.isArray(res) ? res : (res?.data || []));
    } catch (e) {
      console.error("Error loading history", e);
    } finally {
      setLoadingHistory(false);
    }
  };

  const startTest = async () => {
    setStep(3); // Loading state
    try {
      // Fetch JSON from R2
      const url = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/test-languages-300-words/${selectedLang}.json`;
      const res = await fetch(url);
      if (!res.ok) throw new Error("Não foi possível carregar os dados deste idioma.");
      const data: Record<string, string[]> = await res.json();
      
      setTestData(data);
      const map: Record<string, string> = {};
      Object.entries(data).forEach(([lvl, words]) => {
        words.forEach(w => map[w] = lvl);
      });
      setWordLevelMap(map);

      // Prepare 60 words total (10 per level)
      let allSelectedWords: string[] = [];
      CEFR_LEVELS.forEach(lvl => {
        if (data[lvl]) {
          const shuffled = shuffle(data[lvl]);
          allSelectedWords.push(...shuffled.slice(0, 10)); // 10 * 6 = 60 words
        }
      });
      allSelectedWords = shuffle(allSelectedWords);

      setStep1Words(allSelectedWords.slice(0, 30));
      setStep2Words(allSelectedWords.slice(30, 60));
      setKnownWords(new Set());
      setStep(1);

    } catch (e: any) {
      alert("Erro: " + e.message);
      setStep(0);
    }
  };

  const toggleWord = (word: string) => {
    setKnownWords(prev => {
      const next = new Set(prev);
      if (next.has(word)) next.delete(word);
      else next.add(word);
      return next;
    });
  };

  const advanceToStep2 = () => {
    setStep(2);
  };

  const finishTest = async () => {
    if (!user?.id) return;
    setStep(3); // Saving

    const allShownWords = Array.from(new Set([...step1Words, ...step2Words]));
    const totalShown = allShownWords.length;
    const totalKnown = Array.from(knownWords).length;
    const globalAccuracy = totalShown > 0 ? totalKnown / totalShown : 0;

    // Analyze final CEFR level based on combined stats
    const levelCounts: Record<string, { total: number, known: number }> = {};
    CEFR_LEVELS.forEach(l => levelCounts[l] = { total: 0, known: 0 });

    allShownWords.forEach(w => {
      const lvl = wordLevelMap[w];
      if (levelCounts[lvl]) {
        levelCounts[lvl].total++;
        if (knownWords.has(w)) levelCounts[lvl].known++;
      }
    });

    const counts: Record<string, number> = { 
      A1: levelCounts['A1']?.known || 0, 
      A2: levelCounts['A2']?.known || 0, 
      B1: levelCounts['B1']?.known || 0, 
      B2: levelCounts['B2']?.known || 0, 
      C1: levelCounts['C1']?.known || 0, 
      C2: levelCounts['C2']?.known || 0 
    };

    let finalLevel = 'A1'; // Piso mínimo inicial

    // Progressão linear estrita: Só avança se dominar o nível atual
    if (counts.A1 >= 8) {
      finalLevel = 'A1';
      if (counts.A2 >= 8) {
        finalLevel = 'A2';
        if (counts.B1 >= 8) {
          finalLevel = 'B1';
          if (counts.B2 >= 8) {
            finalLevel = 'B2';
            if (counts.C1 >= 8) {
              finalLevel = 'C1';
              if (counts.C2 >= 8) {
                finalLevel = 'C2';
              }
            }
          }
        }
      }
    }

    const levels = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
    const weights: Record<string, number> = { A1: 1000, A2: 1500, B1: 1000, B2: 1500, C1: 3000, C2: 8000 };

    let estimated_words = 0;
    let poolAtivo = true;

    for (const lvl of levels) {
      const accuracy = counts[lvl] / 10;
      
      if (poolAtivo) {
        estimated_words += accuracy * weights[lvl];
        // Assim que computar o nível real do usuário, fecha o pool para os níveis acima
        if (lvl === finalLevel) {
          poolAtivo = false;
        }
      }
    }

    // Aplica o teto máximo de segurança do nível para blindar o layout
    const cefrCeilings: Record<string, number> = { A1: 1000, A2: 2500, B1: 3500, B2: 5000, C1: 8000, C2: 16000 };
    const finalEstimatedWords = Math.min(Math.round(estimated_words), cefrCeilings[finalLevel] || 1000);

    const testId = crypto.randomUUID();

    try {
      // Parent table insert
      await dbManager.from('user_language_tests', user.id).insert({
        id: testId,
        user_id: user.id,
        lang_code: selectedLang,
        estimated_words: finalEstimatedWords,
        cefr_level: finalLevel,
        correct_count: totalKnown,
        incorrect_count: totalShown - totalKnown,
        created_at: new Date().toISOString()
      });

      // 1. Defina o ID do teste pai gerado no insert anterior
      const currentTestId = testId; 

      // 2. FORÇAR A CONVERSÃO: Não importa como as palavras estão salvas no estado
      const wordsPayload = allShownWords.map((p: any) => {
        const wordStr = p.word || p.text || String(p);
        return {
          id: crypto.randomUUID(),
          test_id: currentTestId,
          word: wordStr,
          // Se o seu estado usa um objeto/mapa de marcados, valide aqui
          is_known: knownWords.has(wordStr) ? 1 : 0 
        };
      });

      // 3. AUDITORIA LOCAL: O Selo de Infalibilidade.
      console.log("👉 [FRONTEND AUDIT] Array gerado para envio:", wordsPayload);

      if (!wordsPayload || wordsPayload.length === 0) {
        throw new Error("A lista de palavras gerada está vazia. Verifique o estado do componente.");
      }

      // 4. O DISPARO UNIFICADO: Envia estritamente envelopado na propriedade 'items'
      const response = await dbManager.execute({
        action: 'bulk_insert' as any,
        table: 'user_test_word_details',
        data: { items: wordsPayload },
        user_id: user.id
      });

      // Update roadmap_progress for the tested language and level
      const cleanLang = (selectedLang || 'en').toLowerCase();
      const cleanLevel = (finalLevel || 'A1').toUpperCase();
      await dbManager.from('roadmap_progress', user.id).upsert({
        user_id: user.id,
        language_code: cleanLang,
        current_level: cleanLevel,
        current_topic_index: 0,
        updated_at: new Date().toISOString()
      });

      console.log("✅ Lote de palavras inserido com sucesso!", response);

      setFinalResult({ cefr: finalLevel, words: finalEstimatedWords });
      setStep(4);
    } catch (e: any) {
      console.error(e);
      alert("Erro ao salvar o teste. Tente novamente.");
      setStep(0);
    }
  };

  const getLanguageName = (code: string) => {
    return AVAILABLE_LANGS.find(l => l.code === code)?.name || code.toUpperCase();
  };

  const getFlag = (code: string) => {
    return AVAILABLE_LANGS.find(l => l.code === code)?.flag || '🌍';
  };

  return (
    <div className="min-h-full flex flex-col p-4 md:p-8 max-w-4xl mx-auto w-full">
      <AppBackButton className="mb-6" onClick={() => step === 0 ? goBackOr(navigate, '/personal-hub') : setStep(0)} />

      <AnimatePresence mode="wait">
        {step === 0 && (
          <motion.div
            key="intro"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex flex-col gap-10"
          >
            <div className="space-y-4">
              <StudyPageHeader tourKey="level_test" title={i18nInstance.t('ui.level.myLevel')} />
              <p className="text-sm text-ink/70 leading-relaxed max-w-2xl bg-tint-butter rounded-[22px] px-5 py-4">
                  Meça a dimensão aproximada do seu vocabulário passivo. Selecione as palavras que você conhece pelo menos um significado. <strong className="font-medium text-ink"><T k="ui.level.dontGuess" /></strong>
              </p>
            </div>

            <div data-tour="level-start" className="bg-surface p-6 md:p-8 rounded-[32px] border border-black/[0.06] dark:border-white/[0.06]">
              <h3 className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted mb-5">
                Iniciar um Novo Teste
              </h3>
              
              <div className="flex flex-col sm:flex-row gap-4 items-center">
                <div className="relative w-full sm:w-64">
                  <select 
                    value={selectedLang}
                    onChange={(e) => setSelectedLang(e.target.value)}
                    className="w-full h-12 appearance-none bg-tint-stone border-none text-ink px-5 rounded-full font-medium outline-none focus:ring-2 focus:ring-ink/10 transition-all"
                  >
                    {AVAILABLE_LANGS.map(l => (
                      <option key={l.code} value={l.code}>{l.flag} {l.name}</option>
                    ))}
                  </select>
                  <Globe2 className="absolute right-5 top-1/2 -translate-y-1/2 text-muted pointer-events-none" size={17} strokeWidth={1.75} />
                </div>

                <button 
                  onClick={startTest}
                  className="w-full sm:w-auto h-12 bg-primary text-slate-900 px-7 rounded-full font-medium text-sm flex items-center justify-center gap-2 transition-all hover:opacity-90 active:scale-95"
                >
                  <AnimatedIcon motion="pop"><Sparkles size={16} strokeWidth={1.75} /></AnimatedIcon>
                  Iniciar Teste
                </button>
              </div>
            </div>

            <div data-tour="level-history" className="space-y-4">
              <h3 className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted flex items-center gap-2 px-1">
                <BarChart2 size={13} strokeWidth={1.75} />
                Histórico de Nivelamento
              </h3>

              {loadingHistory ? (
                <div className="flex justify-center p-8">
                  <Loader2 className="w-8 h-8 text-warn animate-spin" />
                </div>
              ) : history.length === 0 ? (
                <div className="bg-surface px-8 py-12 rounded-[32px] border border-black/[0.06] dark:border-white/[0.06] text-center">
                  <div className="w-16 h-16 bg-tint-lavender rounded-full flex items-center justify-center text-ink mx-auto mb-4">
                    <HistoryIcon />
                  </div>
                  <h4 className="font-display text-2xl font-light tracking-[-0.04em] text-ink mb-1">Nenhum teste realizado</h4>
                  <p className="text-muted text-sm"><T k="ui.level.startHint" /></p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {history.map((item) => (
                    <div key={item.id} className="bg-surface p-6 rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] flex justify-between items-center">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xl">{getFlag(item.lang_code)}</span>
                          <span className="font-bold text-sm text-ink">{getLanguageName(item.lang_code)}</span>
                        </div>
                        <p className="text-xs text-slate-500">
                          {new Date(item.created_at).toLocaleDateString()} • {item.correct_count} acertos
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="w-12 h-12 rounded-full bg-tint-sage text-ink font-mono text-sm inline-flex items-center justify-center mb-1">{item.cefr_level}</span>
                        <span className="text-[10px] font-normal text-slate-400 uppercase tracking-[0.14em] font-mono">{item.estimated_words?.toLocaleString()} palavras</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {step === 1 || step === 2 ? (
          <motion.div
            key={`step-${step}`}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="flex-1 flex flex-col justify-between"
          >
            <div>
              <div className="flex justify-between items-center mb-4">
                <span className="text-xs font-normal uppercase font-mono tracking-[0.14em] text-warn">
                  Passo {step} de 2 • {getFlag(selectedLang)} {getLanguageName(selectedLang)}
                </span>
                <span className="text-xs font-bold text-slate-400">
                  {step === 1 ? 'Palavras 1-30' : 'Palavras 31-60'}
                </span>
              </div>

              <h2 className="text-xl font-medium text-ink tracking-[-0.02em] mb-2">
                Selecione apenas as palavras que você sabe o significado:
              </h2>
              <p className="text-xs text-slate-500 mb-6">
                Não chuta! Se não tiver certeza de pelo menos um significado, deixe desmarcada.
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {(step === 1 ? step1Words : step2Words).map((word) => {
                  const isKnown = knownWords.has(word);
                  return (
                    <button
                      key={word}
                      onClick={() => toggleWord(word)}
                      className={`p-3.5 rounded-xl border text-left font-bold text-xs transition-all flex items-center justify-between cursor-pointer ${
                        isKnown
                          ? 'border-warn bg-warn/10 text-warn'
                          : 'border-black/[0.06] dark:border-white/[0.06] bg-surface text-slate-700 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-700'
                      }`}
                    >
                      <span className="truncate">{word}</span>
                      {isKnown && <CheckCircle2 size={16} className="shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-between items-center mt-8 pt-4 border-t border-black/[0.06] dark:border-white/[0.06]">
              {step === 2 ? (
                <button 
                  onClick={() => setStep(1)}
                  className="text-xs font-normal text-slate-500 hover:text-slate-900 dark:hover:text-white uppercase font-mono tracking-[0.14em]"
                >
                  ← Voltar Passo 1
                </button>
              ) : <div />}

              <button
                onClick={step === 1 ? advanceToStep2 : finishTest}
                className="bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-8 py-4 rounded-2xl font-medium flex items-center gap-3 transition-all hover:scale-105 active:scale-95 cursor-pointer"
              >
                {step === 1 ? 'Próximo Passo →' : 'Finalizar Teste'}
              </button>
            </div>
          </motion.div>
        ) : null}

        {step === 3 && (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex-1 flex flex-col items-center justify-center p-12 text-center"
          >
            <RefreshCw className="w-16 h-16 text-warn animate-spin mb-6" />
            <h2 className="text-2xl font-medium text-ink mb-2">Processando resultados...</h2>
            <p className="text-slate-500 font-medium"><T k="ui.level.calculating" /></p>
          </motion.div>
        )}

        {step === 4 && finalResult && (
          <motion.div
            key="result"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex-1 flex flex-col items-center justify-center text-center max-w-lg mx-auto w-full space-y-6"
          >
            <div className="w-24 h-24 bg-tint-sage dark:bg-tint-sage/30 text-ok dark:text-ok rounded-full flex items-center justify-center mx-auto">
              <CheckCircle2 size={48} />
            </div>
            
            <div>
              <h2 className="text-4xl font-medium text-ink mb-2">
                Nível {finalResult.cefr}
              </h2>
              <p className="text-base text-slate-500 font-medium">
                Seu vocabulário passivo estimado é de <strong className="text-ink">{finalResult.words.toLocaleString('pt-BR')} palavras</strong>.
              </p>
            </div>

            <div className="w-full space-y-3 pt-4">
              <button
                onClick={() => { 
                  setStep(0); 
                  setFinalResult(null);
                  setStep1Words([]);
                  setStep2Words([]);
                  setKnownWords(new Set());
                  navigate('/personal-hub/level-test'); 
                }}
                className="w-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-8 py-4 rounded-2xl font-medium transition-all hover:scale-105 active:scale-95 cursor-pointer"
              >
                Voltar ao Início
              </button>
              <button
                onClick={() => {
                  const targetLang = (selectedLang || 'en').toLowerCase();
                  const targetLevel = (finalResult.cefr || 'A1').toLowerCase();
                  navigate(`/roadmap/${targetLang}/${targetLevel}`);
                }}
                className="w-full bg-ok hover:bg-ok text-surface px-8 py-4 rounded-2xl font-medium transition-all hover:scale-105 active:scale-95 cursor-pointer flex items-center justify-center gap-2"
              >
                <span><T k="ui.level.goRoadmap" /></span>
                <ChevronRight size={18} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const HistoryIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
    <path d="M3 3v5h5"/>
    <path d="M12 7v5l4 2"/>
  </svg>
);

export default LevelTestPage;
